import { TkticSelect } from "@/components/ui/TkticSelect";
import type { TeamPerformance } from "@/lib/types";

/** The team page's Season picker (#908 D1). `seasons` are the seasons this
 * team has existed in across its Carry-forward lineage, each with the team
 * that holds it: choosing a season held by another season team opens that
 * team's page (`onOpenTeam`), one held by this team changes this page's
 * season (`onChangeSeason`). A single season is shown as plain text. */
export function TeamSeasonPicker({
  teamSlug,
  selectedSeason,
  seasons,
  onChangeSeason,
  onOpenTeam,
}: {
  teamSlug: string;
  selectedSeason: string | undefined;
  seasons: TeamPerformance["seasons"];
  onChangeSeason: (seasonId: string) => void;
  onOpenTeam: (teamSlug: string, seasonId: string) => void;
}) {
  if (seasons.length <= 1) {
    return (
      <p className="grid gap-1 text-xs" data-testid="team-season-single">
        Season
        <span className="text-sm text-ink">{seasons[0]?.name}</span>
      </p>
    );
  }
  // A team and a season together identify a choice: one season can be held
  // by two of the lineage's teams.
  const encode = (slug: string, seasonId: string) =>
    JSON.stringify([slug, seasonId]);
  return (
    <label className="grid gap-1 text-xs">
      Season
      <TkticSelect
        ariaLabel="Season"
        value={encode(teamSlug, selectedSeason ?? "")}
        options={seasons.map((s) => ({
          value: encode(s.team_slug, s.id),
          label: s.name,
        }))}
        onChange={(choice) => {
          const [slug, seasonId] = JSON.parse(choice) as [string, string];
          if (slug === teamSlug) onChangeSeason(seasonId);
          else onOpenTeam(slug, seasonId);
        }}
      />
    </label>
  );
}
